import { useCallback, useEffect, useEffectEvent, useState } from "react";
import { reportError } from "../services/eventBus";

export type ResourceState<T> = { kind: "loading"; data: T | null } | { kind: "error"; message: string; data: T | null } | { kind: "ready"; data: T };

export interface AsyncResource<T> {
  state: ResourceState<T>;
  data: T | null;
  reload: () => void;
  setData: (updater: (current: T | null) => T | null) => void;
}

type Outcome<T> = { kind: "ready"; data: T } | { kind: "error"; message: string };

interface Settled<T> {
  key: string;
  version: number;
  outcome: Outcome<T>;
}

/**
 * Loads a screen resource with AbortController cancellation, keeps stale data
 * while reloading, reports failures through the event bus, and exposes reload
 * for Retry buttons. `key` is the caller's serialized input (route params,
 * filters); a change reloads. Loading is derived: the last settled result is
 * stale whenever its key or version differs from the current one.
 */
export function useAsyncResource<T>(loader: (signal: AbortSignal) => Promise<T>, key: string, context: string): AsyncResource<T> {
  const [settled, setSettled] = useState<Settled<T> | null>(null);
  const [version, setVersion] = useState(0);
  const runLoader = useEffectEvent((signal: AbortSignal) => loader(signal));

  useEffect(() => {
    const controller = new AbortController();
    const run = async () => {
      try {
        const data = await runLoader(controller.signal);
        if (!controller.signal.aborted) setSettled({ key, version, outcome: { kind: "ready", data } });
      } catch (error) {
        if (controller.signal.aborted) return;
        const reported = reportError(error, context);
        setSettled({ key, version, outcome: { kind: "error", message: reported.message } });
      }
    };
    void run();
    return () => controller.abort();
  }, [key, version, context]);

  const staleData = settled?.outcome.kind === "ready" ? settled.outcome.data : null;
  const isCurrent = settled !== null && settled.key === key && settled.version === version;
  const state: ResourceState<T> = !isCurrent ? { kind: "loading", data: staleData } : settled.outcome.kind === "ready" ? { kind: "ready", data: settled.outcome.data } : { kind: "error", message: settled.outcome.message, data: null };

  const reload = useCallback(() => setVersion((current) => current + 1), []);
  const setData = useCallback(
    (updater: (current: T | null) => T | null) => {
      setSettled((current) => {
        const currentData = current?.outcome.kind === "ready" ? current.outcome.data : null;
        const next = updater(currentData);
        if (next === null || !current) return current;
        return { ...current, outcome: { kind: "ready", data: next } };
      });
    },
    [],
  );

  return { state, data: state.data, reload, setData };
}
